/**
 * useNetworkFeed Hook
 * Fetches network questions via the peer-learning-sync edge function
 * and manages answers/posting via direct Supabase queries.
 *
 * DO NOT modify: postQuestion, postAnswer, markHelpful logic.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAuthenticatedClient, getCurrentUserId } from '@/services';
import { edgeFunctions } from '@/services/edgeFunctions';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/integrations/supabase/types';

type TopicCategory = Database['public']['Enums']['topic_category'];

// ── Constants ─────────────────────────────────────────
const BROILER_CROP_ID = 'broiler'; // fallback crop identifier
const FALLBACK_COUNTRY_UUID = 'b5b4a485-769c-49ed-8d38-3c0b8104f9e5'; // Bangladesh

// ── Types ─────────────────────────────────────────────

interface NetworkQuestion {
  id: string;
  author_id: string | null;
  question_text: string | null;
  audio_url: string | null;
  audio_duration_seconds: number | null;
  topic_category: TopicCategory | null;
  answer_count: number;
  view_count: number;
  created_at: string;
  country_id: string;
  region: string | null;
  author?: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
    village: string | null;
  } | null;
}

interface NetworkAnswer {
  id: string;
  question_id: string;
  author_id: string | null;
  answer_text: string | null;
  audio_url: string | null;
  audio_duration_seconds: number | null;
  helpful_count: number;
  is_accepted: boolean;
  created_at: string;
  author?: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  /** Trust data from peer_answer_statistics (joined) */
  trust_tier: TrustTier;
  trust_badge: string;
  trust_label: string;
  wilson_lower_bound: number | null;
}

/** Trust tier — matches compute-peer-stats 5-tier output */
export type TrustTier = 'canonical' | 'established' | 'valid' | 'emerging' | 'unvalidated';

export const TRUST_TIER_DISPLAY: Record<TrustTier, { emoji: string; label: string; variant: string }> = {
  canonical:   { emoji: '\u2705', label: 'network.expertVerified',   variant: 'default' },
  established: { emoji: '\u2B50', label: 'network.communityTrusted', variant: 'default' },
  valid:       { emoji: '\uD83D\uDC4D', label: 'network.helpful',          variant: 'secondary' },
  emerging:    { emoji: '\uD83C\uDF31', label: 'network.newInsight',        variant: 'outline' },
  unvalidated: { emoji: '\u26A0\uFE0F', label: 'network.unverified',        variant: 'outline' },
};

// ── Batch context helper ──────────────────────────────

async function getActiveBatchContext(): Promise<{ batchDay: number; batchId: string } | null> {
  const userId = getCurrentUserId();
  if (!userId) return null;
  try {
    const client = getAuthenticatedClient();
    const { data } = await client
      .from('batches')
      .select('id, placement_date')
      .eq('user_id', userId)
      .in('batch_status', ['placed', 'active'])
      .order('placement_date', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!data) return null;
    const placed = new Date(data.placement_date);
    const batchDay = Math.max(1, Math.floor((Date.now() - placed.getTime()) / 86400000) + 1);
    return { batchDay, batchId: data.id };
  } catch {
    return null;
  }
}

// ── Hook ──────────────────────────────────────────────

export function useNetworkFeed(topicCategory?: TopicCategory) {
  const queryClient = useQueryClient();
  const client = getAuthenticatedClient();
  const userId = getCurrentUserId();
  const { user } = useAuth();

  // Derive the country slug (lowercase ISO) for the edge function call.
  // Falls back to Bangladesh if the user has no countryCode yet.
  const countrySlug = (user?.countryCode || 'BD').toLowerCase();

  // Look up the matching country UUID for the fallback direct query.
  // Cached separately by country code so it only runs once per country.
  async function resolveCountryUuid(): Promise<string> {
    if (!user?.countryCode) return FALLBACK_COUNTRY_UUID;
    try {
      const { data } = await client
        .from('countries')
        .select('id')
        .eq('code', user.countryCode)
        .maybeSingle();
      return (data?.id as string) || FALLBACK_COUNTRY_UUID;
    } catch {
      return FALLBACK_COUNTRY_UUID;
    }
  }

  // ── Fetch questions via edge function ───────────────
  const questionsQuery = useQuery({
    queryKey: ['network-questions', topicCategory, countrySlug],
    queryFn: async (): Promise<NetworkQuestion[]> => {
      // Get batch context for the current farmer
      const batchCtx = await getActiveBatchContext();

      // Build search query
      const queryText = topicCategory
        ? `category:${topicCategory}`
        : ''; // empty = trending

      const { data, error } = await edgeFunctions.peerLearningSync({
        queryText,
        cropId: BROILER_CROP_ID,
        countryId: countrySlug,
        batchDayContext: batchCtx?.batchDay,
        caller: 'network_screen',
      });

      // If edge function fails, fall back to direct query
      if (error || !data) {
        console.warn('peerLearningSync failed, falling back to direct query:', error);
        return fallbackDirectQuery(topicCategory);
      }

      // Edge function returns { answers: [...], totalResults }
      // Map the edge function response to our NetworkQuestion shape
      const questions: NetworkQuestion[] = (data.answers || []).map((a: any) => ({
        id: a.id,
        author_id: null,
        question_text: a.text,
        audio_url: null,
        audio_duration_seconds: null,
        topic_category: topicCategory || null,
        answer_count: 0,
        view_count: a.helpfulCount || 0,
        created_at: new Date().toISOString(),
        country_id: countrySlug,
        region: null,
        author: a.authorName ? { id: '', full_name: a.authorName, avatar_url: null, village: null } : null,
      }));

      // If the edge function returned results, use them.
      // Otherwise fall back to direct query for full data.
      if (questions.length > 0) return questions;
      return fallbackDirectQuery(topicCategory);
    },
    staleTime: 1000 * 60,
  });

  // Fallback: direct Supabase query (preserves original behavior)
  async function fallbackDirectQuery(category?: TopicCategory): Promise<NetworkQuestion[]> {
    const countryUuid = await resolveCountryUuid();
    let query = client
      .from('network_questions')
      .select(`
        id,
        author_id,
        question_text,
        audio_url,
        audio_duration_seconds,
        topic_category,
        answer_count,
        view_count,
        created_at,
        country_id,
        region,
        author:profiles!network_questions_author_id_fkey(id, full_name, avatar_url, village)
      `)
      .is('deleted_at', null)
      .eq('country_id', countryUuid)
      .order('created_at', { ascending: false })
      .limit(50);

    if (category) {
      query = query.eq('topic_category', category);
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data || []).map(item => ({
      ...item,
      author: Array.isArray(item.author) ? item.author[0] : item.author,
    })) as NetworkQuestion[];
  }

  // ── Fetch answers for a specific question ───────────
  const useAnswers = (questionId: string) => {
    return useQuery({
      queryKey: ['network-answers', questionId],
      queryFn: async (): Promise<NetworkAnswer[]> => {
        const { data, error } = await client
          .from('network_answers')
          .select(`
            id,
            question_id,
            author_id,
            answer_text,
            audio_url,
            audio_duration_seconds,
            helpful_count,
            is_accepted,
            created_at,
            author:profiles!network_answers_author_id_fkey(id, full_name, avatar_url),
            peer_answer_statistics(
              trust_tier, trust_badge, trust_label, wilson_lower_bound,
              helpful_count, unhelpful_count, display_text
            )
          `)
          .eq('question_id', questionId)
          .is('deleted_at', null)
          .order('helpful_count', { ascending: false });

        if (error) throw error;

        return (data || []).map(item => {
          const pas = Array.isArray(item.peer_answer_statistics)
            ? item.peer_answer_statistics[0]
            : item.peer_answer_statistics;
          return {
            ...item,
            author: Array.isArray(item.author) ? item.author[0] : item.author,
            trust_tier: (pas?.trust_tier as TrustTier) ?? 'unvalidated',
            trust_badge: pas?.trust_badge ?? '\u26A0\uFE0F',
            trust_label: pas?.trust_label ?? 'Unverified',
            wilson_lower_bound: pas?.wilson_lower_bound ?? null,
          };
        }) as NetworkAnswer[];
      },
      enabled: !!questionId,
    });
  };

  // ── Post a new question (unchanged) ─────────────────
  const postQuestion = useMutation({
    mutationFn: async (data: {
      questionText: string;
      audioUrl?: string;
      audioDuration?: number;
      topicCategory?: TopicCategory;
      countryId: string;
    }) => {
      if (!userId) throw new Error('Not authenticated');

      const { error } = await client
        .from('network_questions')
        .insert({
          author_id: userId,
          question_text: data.questionText,
          audio_url: data.audioUrl,
          audio_duration_seconds: data.audioDuration,
          topic_category: data.topicCategory,
          country_id: data.countryId,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['network-questions'] });
      toast.success('Question posted!');
    },
    onError: (error) => {
      toast.error('Failed to post question');
      console.error('Post question error:', error);
    },
  });

  // ── Post an answer (unchanged) ──────────────────────
  const postAnswer = useMutation({
    mutationFn: async (data: {
      questionId: string;
      answerText: string;
      audioUrl?: string;
      audioDuration?: number;
    }) => {
      if (!userId) throw new Error('Not authenticated');

      const { error } = await client
        .from('network_answers')
        .insert({
          question_id: data.questionId,
          author_id: userId,
          answer_text: data.answerText,
          audio_url: data.audioUrl,
          audio_duration_seconds: data.audioDuration,
        });

      if (error) throw error;

      // Increment answer count on question
      const { error: updateError } = await client
        .from('network_questions')
        .update({ answer_count: questionsQuery.data?.find(q => q.id === data.questionId)?.answer_count ?? 0 + 1 })
        .eq('id', data.questionId);

      if (updateError) console.error('Failed to update answer count:', updateError);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['network-answers', variables.questionId] });
      queryClient.invalidateQueries({ queryKey: ['network-questions'] });
      toast.success('Answer posted! +25 points');
    },
    onError: (error) => {
      toast.error('Failed to post answer');
      console.error('Post answer error:', error);
    },
  });

  // ── Mark answer as helpful — writes to helpful_votes + triggers stats recompute ──
  const markHelpful = useMutation({
    mutationFn: async (answerId: string) => {
      if (!userId) throw new Error('Not authenticated');

      // Check if user already voted
      const { data: existingVote } = await client
        .from('helpful_votes')
        .select('id, is_negative')
        .eq('user_id', userId)
        .eq('answer_id', answerId)
        .maybeSingle();

      if (existingVote && !existingVote.is_negative) {
        // Already voted positive — no double-voting
        return;
      }

      if (existingVote && existingVote.is_negative) {
        // Flip negative vote to positive
        await client
          .from('helpful_votes')
          .update({ is_negative: false })
          .eq('id', existingVote.id);
      } else {
        // Insert new positive vote
        const { error: insertErr } = await client
          .from('helpful_votes')
          .insert({ user_id: userId, answer_id: answerId, is_negative: false });
        if (insertErr) throw insertErr;
      }

      // Also increment network_answers.helpful_count for backward compat sort order
      const { data: answer } = await client
        .from('network_answers')
        .select('helpful_count')
        .eq('id', answerId)
        .single();

      if (answer) {
        await client
          .from('network_answers')
          .update({ helpful_count: (answer.helpful_count || 0) + 1 })
          .eq('id', answerId);
      }

      // Trigger peer stats recompute (fire-and-forget)
      client.functions.invoke('compute-peer-stats', {
        body: { action: 'compute_answer', answer_id: answerId },
      }).catch(() => {/* non-critical */});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['network-answers'] });
      toast.success('Marked as helpful!');
    },
    onError: () => {
      toast.error('Failed to mark as helpful');
    },
  });

  return {
    questions: questionsQuery.data ?? [],
    isLoading: questionsQuery.isLoading,
    error: questionsQuery.error,
    refetch: questionsQuery.refetch,

    useAnswers,
    postQuestion,
    postAnswer,
    markHelpful,
  };
}

export type { NetworkQuestion, NetworkAnswer };
